import type { SafeZoneSpec } from '../../data/safe-zones';

/** Shared styling so the inline SVG preview and the exported PNG look identical. */
export const OVERLAY_STYLE = {
  marginFill: 'rgba(15, 23, 42, 0.55)',
  safeStroke: '#22c55e',
  uiStroke: 'rgba(255, 255, 255, 0.9)',
  labelFill: '#ffffff',
} as const;

export const getSafeRect = (spec: SafeZoneSpec) => ({
  x: spec.margins.left,
  y: spec.margins.top,
  width: spec.canvas.width - spec.margins.left - spec.margins.right,
  height: spec.canvas.height - spec.margins.top - spec.margins.bottom,
});

/** Font size for labels, proportional to the canvas so 1080- and 2560-wide specs read the same. */
const labelSize = (spec: SafeZoneSpec) => Math.round(spec.canvas.width / 40);

/** Draws the overlay (shaded margins, safe-area outline, labelled UI areas) at full spec resolution. */
export function drawSafeZoneOverlay(ctx: CanvasRenderingContext2D, spec: SafeZoneSpec) {
  const { width: W, height: H } = spec.canvas;
  const safe = getSafeRect(spec);
  const stroke = Math.max(2, Math.round(W / 360));

  ctx.save();
  ctx.fillStyle = OVERLAY_STYLE.marginFill;
  ctx.fillRect(0, 0, W, safe.y);
  ctx.fillRect(0, safe.y + safe.height, W, H - safe.y - safe.height);
  ctx.fillRect(0, safe.y, safe.x, safe.height);
  ctx.fillRect(safe.x + safe.width, safe.y, W - safe.x - safe.width, safe.height);

  ctx.lineWidth = stroke;
  ctx.strokeStyle = OVERLAY_STYLE.safeStroke;
  ctx.strokeRect(safe.x, safe.y, safe.width, safe.height);

  ctx.setLineDash([stroke * 4, stroke * 3]);
  ctx.strokeStyle = OVERLAY_STYLE.uiStroke;
  ctx.fillStyle = OVERLAY_STYLE.labelFill;
  ctx.font = `600 ${labelSize(spec)}px system-ui, sans-serif`;
  ctx.textBaseline = 'top';
  for (const rect of spec.uiElements) {
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    if (rect.label) {
      ctx.fillText(rect.label, rect.x + stroke * 4, rect.y + stroke * 4, Math.max(rect.width - stroke * 8, 40));
    }
  }
  ctx.restore();
}

function triggerDownload(canvas: HTMLCanvasElement, filename: string) {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }, 'image/png');
}

/** Transparent PNG of the overlay alone, at the platform's canvas size. */
export function downloadOverlayPng(spec: SafeZoneSpec) {
  const canvas = document.createElement('canvas');
  canvas.width = spec.canvas.width;
  canvas.height = spec.canvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  drawSafeZoneOverlay(ctx, spec);
  triggerDownload(canvas, `${spec.slug.replace(/-checker$/, '')}-overlay.png`);
}

/** Uploaded image or current video frame (contained on black, like the preview) with the overlay on top. */
export function downloadCompositePng(spec: SafeZoneSpec, media: HTMLImageElement | HTMLVideoElement, name: string) {
  const { width: W, height: H } = spec.canvas;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const mediaW = media instanceof HTMLVideoElement ? media.videoWidth : media.naturalWidth;
  const mediaH = media instanceof HTMLVideoElement ? media.videoHeight : media.naturalHeight;
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, W, H);
  if (mediaW && mediaH) {
    const scale = Math.min(W / mediaW, H / mediaH);
    const drawW = mediaW * scale;
    const drawH = mediaH * scale;
    ctx.drawImage(media, (W - drawW) / 2, (H - drawH) / 2, drawW, drawH);
  }
  drawSafeZoneOverlay(ctx, spec);
  triggerDownload(canvas, `${name}-${spec.slug.replace(/-checker$/, '')}.png`);
}
